import { Application } from '../application/application';
import { ContainerDefinition, Container, REQUEST_ATTRIB_DOMAIN, REQUEST_ATTRIB_CONTAINER } from '../panel/cabinets';
import { ContainerPanel } from '../panel/panel';
import { VisualizerControl, Visualizer, VisualizationUpdateLevel } from '../data-source/visualizer';
import { AnimationMode } from '../data-source/types';
import { getDataSource, getImpactMonitorDataSource, ModelingDataSource, ModelingDataList, ImpactMonitorDatum, setImpactScore } from './modeling';
import { ModelingTypeDefinition, ModelProject } from './modeling-common';
import { ModelingInfoVisualizer } from './mdl-ii-visualizer';
import { CombineMode, RenderMode, ColorMode } from '../worker/scalar-field-manager';
import { ImpactMap } from '../scalar-field/scalar-field';
import { CacheEntry } from '../worker/scalar-field-worker';
import { ModelingResultVisualizer } from './mdl-mr-visualizer';
import { WindFieldMapVisualizer } from './mdl-wf-visualizer';
import { getPolygonArea, getPolygonCCW, Polygon } from '../geometry/polygon';
import { Coordinate } from '../map/map-types';
import { SetBuilder } from '../geometry/sets';
import { ImpactMonitorPolygon } from '../worker//scalar-field-worker';
import { Point2D } from '../geometry/geometry';
import { ModellingGrid } from '@mapp/r-mapp-lib';

export const createModelingContainerDefinition = (): ContainerDefinition => {
	return {
		build: (panel: ContainerPanel, containerId: string, application: Application): Container => {
			return new ModelingContainer(panel, containerId, application);
		},
		url: '/' + REQUEST_ATTRIB_DOMAIN + '/modeling/template/container-modeling/' + REQUEST_ATTRIB_CONTAINER,
		cabinets: [
			{ name: 'cab0', drawers: [{ name: 'record' }] }//,
			//      { name: 'cab1', drawers: [{ name: 'list' }, { name: 'statistics' }] }
		]
	}
}

enum ModelingFunctionality {
	manage = 'manage',
	profile = 'profile',
	result = 'result',
	analysis = 'analysis',
	export = 'export'
}

export interface ModelingVisualizerControl extends VisualizerControl {
	combineMode: (visualizerKey: string) => CombineMode
	renderMode: (visualizerKey: string) => RenderMode
	colorMode: (visualizerKey: string) => ColorMode
	projectsToRender: () => string[];
}

export class ModelingContainer extends Container implements ModelingVisualizerControl {

	public static SCALAR_IMPACT_MINIMUM = 0;
	public static SCORE_WEIGHT_SCALAR = 10;
	public static SCORE_WEIGHT_SIZE = 2.5;

	private modelingTypeDefinitionId: string;

	private resultVisualizer: ModelingResultVisualizer;
	private infoVisualizer: ModelingInfoVisualizer;
	private windFieldMapVisualizer: WindFieldMapVisualizer;

	public setImpactScores: (cacheEntry: CacheEntry, grid: ModellingGrid) => void;

	constructor(panel: ContainerPanel, containerId: string, app: Application) {
		super(panel, containerId, app);

		let self = this;
		this.setImpactScores = (cacheEntry: CacheEntry, grid: ModellingGrid): void => {
			if (cacheEntry && cacheEntry.representations) {
				Object.values(cacheEntry.representations)
					.filter(representation => representation.impacts)
					.forEach(representation => {
						self.setImpactScore(representation.impacts, grid );
					})
			}
		}
	}

	public enabled(visualizerKey: string): boolean {
		let enabler = <HTMLInputElement>this.getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);
		if (enabler) {
			return enabler.checked;
		} else {
			console.warn(`Visualizer key ${visualizerKey} could not be found in modeling settings section for type ${this.modelingTypeDefinitionId}`);
		}
	}
	public setEnabled(visualizerKey: string, enabled: boolean): void {
		let enabler = <HTMLInputElement>this.getContainerNode().querySelector(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-enabler input`);
		if (enabler) {
			if (enabler.checked != enabled) {
				enabler.checked = enabled;
				this.enableVisualizer(visualizerKey, enabled);
			}
		} else {
			console.warn(`Visualizer key ${visualizerKey} could not be found in modeling settings section for type ${this.modelingTypeDefinitionId}`);
		}
	}

	public animationMode(visualizerKey: string): AnimationMode {
		if (visualizerKey.startsWith('maptimeline')) {
			//not actually in use now but we need it when we support the footprint mode 
			return AnimationMode.under;
		} else if (visualizerKey.startsWith('source')) {
			return AnimationMode.under;
		}
		return undefined;
	}

	public combineMode(visualizerKey: string): CombineMode {
		if (visualizerKey.startsWith('maptimeline')) {
			let combineMode =
				Array.from(this.getContainerNode().querySelectorAll(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-foldout.maptimeline-viewer .tlmd-radio-group.combine input`))
					.filter((inputElem: HTMLInputElement) => {
						return inputElem.checked;
					})
					.reduce((_mode: string, inputElem: HTMLInputElement) => {
						return inputElem.value;
					}, CombineMode.all);

			return (<any>CombineMode)[combineMode];
		}

		return undefined;
	}
	public renderMode(visualizerKey: string): RenderMode {
		if (visualizerKey.startsWith('maptimeline')) {
			let renderMode =
				Array.from(this.getContainerNode().querySelectorAll(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-foldout.maptimeline-viewer .tlmd-radio-group.render input`))
					.filter((inputElem: HTMLInputElement) => {
						return inputElem.checked;
					})
					.reduce((_mode: string, inputElem: HTMLInputElement) => {
						return inputElem.value;
					}, RenderMode.line_band);

			return (<any>RenderMode)[renderMode];
		}

		return undefined;
	}
	public colorMode(visualizerKey: string): ColorMode {
		if (visualizerKey.startsWith('maptimeline')) {
			let combineMode = this.combineMode(visualizerKey);
			if (combineMode === CombineMode.all) {
				Array.from(this.getContainerNode().querySelectorAll(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-foldout.maptimeline-viewer .tlmd-radio-group.color input`))
					.forEach((inputElem: HTMLInputElement) => {
						inputElem.checked = inputElem.value == ColorMode.palette;
					});
			}
			let colorMode =
				Array.from(this.getContainerNode().querySelectorAll(`div.tlmd-content.settings .tlmd-viewer-settings.${visualizerKey} .tlmd-foldout.maptimeline-viewer .tlmd-radio-group.color input`))
					.filter((inputElem: HTMLInputElement) => {
						return inputElem.checked;
					})
					.reduce((_mode: string, inputElem: HTMLInputElement) => {
						return inputElem.value;
					}, ColorMode.palette);

			return (<any>ColorMode)[colorMode];
		}

		return undefined;
	}
	public projectsToRender(): string[] {
		return Array.from(this.getContainerNode().querySelectorAll('div.tlmd-content.info div.tlmd-projects div.tlmd-project-details.project div.tlmd-enabler span.tlmd-enable-viewer input'))
			.filter((checkBox: HTMLInputElement) => checkBox.checked)
			.map((checkBox: HTMLInputElement) => {
				return checkBox.parentElement.parentElement.parentElement.dataset.project;
			});
	}

	/**
	 * This one gets called once the skeleton markup is available
	 */
	protected finishContainerBuild(): Promise<Container> {
		let modelingTypeDefinition = <ModelingTypeDefinition>JSON.parse((<HTMLDivElement>this.getContainerNode().firstElementChild).dataset.definition);
		//console.info('modelingType=%o', modelingTypeDefinition);

		let modelingFunctionality = <ModelingFunctionality[]>JSON.parse((<HTMLDivElement>this.getContainerNode().firstElementChild).dataset.available);
		//console.info('modelingFunctionality=%o', modelingFunctionality);
		
		//set the modellingGrid property. I.e. construct a ModellingGrid from the type's grid parameters
		modelingTypeDefinition. modellingGrid = new ModellingGrid(modelingTypeDefinition.grid);

		//let our data source know that this type needs to be handled as well.
		this.modelingTypeDefinitionId = getDataSource().addType(modelingTypeDefinition, this);

		getImpactMonitorDataSource().addControlContainer(this.modelingTypeDefinitionId);

		this.addContainerDelegatedSubscription(
			'change',
			'div.tlmd-content.settings .tlmd-viewer-settings.maptimeline .tlmd-foldout input',
			evAndTrgt => {
				evAndTrgt.event.preventDefault();

				let viewerSettingsContainer = (<HTMLElement>evAndTrgt.target.closest('.tlmd-viewer-settings'));
				let visualizerKey = viewerSettingsContainer.dataset.viewer;
				let visualizer: Visualizer = getDataSource().getVisualizer(visualizerKey);
				if (visualizer) {
					if (visualizerKey.startsWith('maptimeline')) {
						visualizer.refresh(VisualizationUpdateLevel.settings);
					} else {
						console.warn(`Unknown setting for visualizer key ${visualizerKey} in modeling settings section for type ${this.modelingTypeDefinitionId} found`);
					}

				} else {
					console.warn(`Unknown visualizer key ${visualizerKey} in modeling settings section for type ${this.modelingTypeDefinitionId} found`);
				}

			});
		this.addContainerDelegatedSubscription(
			'change',
			'div.tlmd-content.info div.tlmd-project-details div.tlmd-enabler span.tlmd-enable-viewer input',
			evAndTrgt => {
				evAndTrgt.event.preventDefault();
				let checked = (<HTMLInputElement>evAndTrgt.target).checked;

				let projectDetailsNode = <HTMLDivElement>evAndTrgt.target.parentElement.parentElement.parentElement;
				let projects = [];
				if (projectDetailsNode.classList.contains('all')) {
					//toggle all projects
					this.getContainerNode().querySelectorAll('div.tlmd-content.info div.tlmd-project-details div.tlmd-enabler span.tlmd-enable-viewer input').forEach((inputElem: HTMLInputElement) => inputElem.checked = checked);
				} else if (projectDetailsNode.classList.contains('category')) {
					//toggle all projects of category
					let category = projectDetailsNode.dataset.category;
					projectDetailsNode.parentElement.querySelectorAll('div.tlmd-category-projects div.tlmd-project-details div.tlmd-enabler span.tlmd-enable-viewer input').forEach((inputElem: HTMLInputElement) => inputElem.checked = checked);

					//projects = this.getDataList().getCategorizedModelProjects().get(category);
					//toggle all projects of category
					if (checked) {
						//set  all to checked
						this.getContainerNode().querySelectorAll('div.tlmd-content.info div.tlmd-project-details.all div.tlmd-enabler span.tlmd-enable-viewer input').forEach((inputElem: HTMLInputElement) => inputElem.checked = checked);
					}
				} else {
					let project = projectDetailsNode.dataset.project;
					let category = projectDetailsNode.parentElement.parentElement.dataset.category;
					if (checked) {
						//set category to checked
						this.getContainerNode().querySelectorAll(`div.tlmd-content.info div.tlmd-model-category-info.category_${category} div.tlmd-project-details.category div.tlmd-enabler span.tlmd-enable-viewer input`).forEach((inputElem: HTMLInputElement) => inputElem.checked = checked);
						//set all to checked
						this.getContainerNode().querySelectorAll('div.tlmd-content.info div.tlmd-project-details.all div.tlmd-enabler span.tlmd-enable-viewer input').forEach((inputElem: HTMLInputElement) => inputElem.checked = checked);
					}

				}
				console.log(this.projectsToRender());
				//notify renderer 
				this.resultVisualizer.refresh(VisualizationUpdateLevel.settings)
			});

		this.infoVisualizer = <ModelingInfoVisualizer>getDataSource().addVisualizer(new ModelingInfoVisualizer('info', this.modelingTypeDefinitionId, this.getDrawer(0, 0)));
		this.resultVisualizer = <ModelingResultVisualizer>getDataSource().addVisualizer(new ModelingResultVisualizer('maptimeline', this.modelingTypeDefinitionId, this.getDrawer(0, 0), `tlmd-modelproject_${this.modelingTypeDefinitionId}`));
		this.windFieldMapVisualizer = <WindFieldMapVisualizer>getDataSource().addVisualizer(new WindFieldMapVisualizer('windfield', this.modelingTypeDefinitionId, this.getDrawer(0, 0)));

		getDataSource().getScalarFieldManager(this.modelingTypeDefinitionId).setCachProcessor(this.setImpactScores);


		return new Promise<Container>((resolve, _reject) => {
			resolve(this);
		});

	}
	public release(): Container {
		getDataSource().getScalarFieldManager(this.modelingTypeDefinitionId)?.setCachProcessor(undefined);

		getImpactMonitorDataSource().removeControlContainer(this.modelingTypeDefinitionId);

		if (this.infoVisualizer) {
			getDataSource().removeVisualizer(this.infoVisualizer.key());
			this.infoVisualizer.release();
		}

		//console.trace(`releasing modeling ${this.getContainerNodeSelector()} container`);
		if (this.resultVisualizer) {
			getDataSource().removeVisualizer(this.resultVisualizer.key());
			this.resultVisualizer.release();
		}

		if (this.windFieldMapVisualizer) {
			getDataSource().removeVisualizer(this.windFieldMapVisualizer.key());
			this.windFieldMapVisualizer.release();
		}

		getDataSource().removeType(this.modelingTypeDefinitionId);

		return super.release();
	}
	public onFocus(): void {
	}

	public onBlur(): void {
		if (this.modelingTypeDefinitionId) {
			getDataSource().onBlur(this.modelingTypeDefinitionId);
		}
	}

	public getDefinition(): ModelingTypeDefinition {
		return <ModelingTypeDefinition>getDataSource().getDefinitions()[this.modelingTypeDefinitionId];
	}


	public getDataSource(): ModelingDataSource {
		return getDataSource();
	}

	public getImpactMonitors(grid: ModellingGrid): ImpactMonitorDatum[] {
		return  processImpactMonitors(<ImpactMonitorDatum[]>getImpactMonitorDataSource().getData()['any'].getData(), grid);
	}
	
	public getImpactMonitorsMap( grid: ModellingGrid): { [key: string]: ImpactMonitorDatum } {
		return this.getImpactMonitors(grid).reduce((map, goi) => {
				map[goi.id] = goi;
				return map;
			}, <{ [key: string]: ImpactMonitorDatum }>{});
	}

	public getDataList(): ModelingDataList {
		return this.getDataSource().getData()[this.modelingTypeDefinitionId];
	}

	public setImpactScore(impacts: ImpactMap, grid: ModellingGrid): void {
		//		  setImpactScore(impacts, this.getImpactMonitorsMap(), this.getDefinition().template.parameters.impact);
		setImpactScore(impacts, this.getImpactMonitorsMap(grid), this.getDefinition().impact);
	}

	public enableVisualizer(visualizerKey: string, _enable: boolean): void {
		if (visualizerKey.startsWith('monitors')) {
			let visualizer: Visualizer = getImpactMonitorDataSource().getVisualizer('monitors');
			if (visualizer) {
				visualizer.refresh(VisualizationUpdateLevel.settings);
			} else {
				console.warn(`Unknown visualizer key ${visualizerKey} in modeling settings section for type ${this.modelingTypeDefinitionId} found`);
			}
		} else {
			let visualizer: Visualizer = getDataSource().getVisualizer(visualizerKey);
			if (visualizer) {
				visualizer.refresh(VisualizationUpdateLevel.settings);
			} else {
				console.warn(`Unknown visualizer key ${visualizerKey} in modeling settings section for type ${this.modelingTypeDefinitionId} found`);
			}
		}
	}

}

/**
 * Adjusts the size and thresholdSize properties for arae Impact Monitors.
 * The size needs to be udjusted in 2 cases:
 * -1 : The impact Monitor lies (partialy) ooutside the modelling grid
 * -2: The exterior of the impact monitor needs to be monitored (instead of the interior)
 * 
 * In both cases it depends on the grid how these properties needs to be modified. Hence the grid is given as parameter.
 * 
 * @param impactMonitors The impact monitors to process
 * @param grid The Modelling Grid that defines the outer boundaries of the modelling domain
 * @return The processed Impact Monitors
 */
export const  processImpactMonitors = ( impactMonitors: ImpactMonitorDatum[], grid: ModellingGrid): ImpactMonitorDatum[] => {
	let vertices: Point2D[] = [].concat(<number[][]>grid.geometry.xy);
	//remove the last coordinate since it equals the first and
	//is undesired for the Polygon implementation;
	vertices.pop();
	let paths = [Array.from(Array(vertices.length).keys())];
	//create a polygon for currrent goi
	let gridPolygon = new Polygon(getPolygonCCW(vertices), paths);
	
	
	return impactMonitors
		.map(goi => {
			//The size property needs to be set to the intersection size  between the goi and the grid being used in this  modelling type
			//When the exterior of the goi needs to be monitored (isHole == true) the size needs to be modified as well
			if ((<number[]>goi.geometry.xy[0]).push) {
				//only area impact monitors have a size so filter on those 
				let vertices: Point2D[] = [].concat(<number[][]>goi.geometry.xy);
				//remove the last coordinate since it equals the first and
				//is undesired for the Polygon implementation;
				vertices.pop();
				let paths = [Array.from(Array(vertices.length).keys())];
				//create a polygon for currrent goi
				let goiPolygon = new Polygon(getPolygonCCW(vertices), paths);

				//determine the intersection between grid and goiPolygon
				let setBuilder = new SetBuilder([goiPolygon, gridPolygon]);
				let intersection = setBuilder.getIntersection();

				//calculate the size of the intersection
				let intersectionSize = 0.0;
				if (intersection) {
					intersectionSize += intersection.paths
						.map((_path, index) => getPolygonArea(intersection.getPathVertices(index)))
						.reduce((siSize, size) => siSize + size, 0);
				}

				if (!goi.isHole) {//goi is a'normal'. I.e. the interior needs to be checked for impacts
					if (goi.size === intersectionSize) {
						//the goi lies  completely inside the grid; 
						return goi;
					} else {
						//return a shallow copy of the goi with it's size property equal to the intersection size
						return Object.assign(
							{}, 
							goi, 
							{
								 size: intersectionSize ,
								 thresholdSize: intersectionSize /goi.properties.impactMonitor.sensitivity
							 });
					}
				} else {// goi's exterior needs to be Checked for impacts
					//return a shallow copy of the goi with it's size property equal to thegrid size minus the  intersection size
					return Object.assign(
						{},
						 goi, 
						 { 
							size: grid.geometry.size - intersectionSize,
						    thresholdSize: (grid.geometry.size - intersectionSize) /goi.properties.impactMonitor.sensitivity
						});
				}
			} else {
				//goi is a location just return
				return goi;
			}
		});
}
